import * as XLSX from 'xlsx-js-style';
import api from '../../../auth/apiClient';
import { isAxiosError } from 'axios';
import { format } from 'date-fns';
import { id } from 'date-fns/locale';
import { trackDownload } from '../../../activity';

interface InspectionData {
    users_estate: string;
    estate: string;
    job_title: string | null;
    nip: string;
    name: string;
    division: string | null;
    block: string;
    spv_name: string | null;
    job_level: string;
    job_title_supervisor: string | null; // Job title dari Supervisor
    penalty_code: string | null;
    penalty_descr: string | null;
    qty: number | null;
    inspection_date: string | null;
    no_tph: string | null;
    no_baris: string | null;
    no_pokok: string | null;
    description: string | null;
}

interface SupervisiPinaltiAncakPanenProps {
    selectedMonth: string;
    selectedEstate: number | null;
    setIsLoading: (loading: boolean) => void;
    months: Array<{ value: string; label: string }>;
    toast: any;
}

const SupervisiPinaltiAncakPanen = {
    export: async ({ selectedMonth, selectedEstate, setIsLoading, months, toast }: SupervisiPinaltiAncakPanenProps) => {

        try {
            const params: any = { month: selectedMonth };
            if (selectedEstate) {
                params.estate_id = selectedEstate;
            }
            const response = await api.get<InspectionData[]>(
                '/api/inspection/report-supervision',
                { params }
            );

            if (!response.data || response.data.length === 0) {
                toast({
                    title: 'Info',
                    description: 'Tidak ada data untuk dilaporkan pada periode terpilih.',
                    status: 'info',
                    duration: 3000,
                    isClosable: true,
                });
                setIsLoading(false);
                return;
            }

            const reportData: InspectionData[] = response.data;

            // 1. Kelompokkan data berdasarkan estate
            const groupedByEstate = reportData.reduce((acc, item) => {
                // const estateKey = item.users_estate || item.estate || 'UNDEFINED_ESTATE';
                const estateKey = item.estate || 'UNDEFINED_ESTATE';
                if (!acc[estateKey]) {
                    acc[estateKey] = [];
                }
                acc[estateKey].push(item);
                return acc;
            }, {} as Record<string, InspectionData[]>);

            const wb = XLSX.utils.book_new();

            const columnOrder = [
                'NAMA KARYAWAN',
                'NIP',
                'JABATAN',
                'DIVISI',
                'BLOK',
                'TANGGAL INSPEKSI',
                'NAMA SUPERVISOR',
                'LEVEL SUPERVISOR',
                'JABATAN SUPERVISOR',
                'KODE PENALTY',
                'DESKRIPSI PENALTY',
                'QTY',
                'NO TPH',
                'NO BARIS',
                'NO POKOK',
                'DESKRIPSI TEMUAN',
            ];

            const headerStyle = {
                font: { bold: true, color: { rgb: "FFFFFF" } },
                fill: { type: "pattern", pattern: "solid", fgColor: { rgb: "4A5A33" } },
                alignment: { horizontal: "center", vertical: "center" },
                border: {
                    top: { style: "thin", color: { rgb: "000000" } },
                    bottom: { style: "thin", color: { rgb: "000000" } },
                    left: { style: "thin", color: { rgb: "000000" } },
                    right: { style: "thin", color: { rgb: "000000" } }
                }
            };

            const cellBorderStyle = {
                border: {
                    top: { style: "thin", color: { rgb: "000000" } },
                    bottom: { style: "thin", color: { rgb: "000000" } },
                    left: { style: "thin", color: { rgb: "000000" } },
                    right: { style: "thin", color: { rgb: "000000" } }
                }
            };

            const centerAlignment = { alignment: { horizontal: "center", vertical: "center" } };
            const leftAlignment = { alignment: { horizontal: "left", vertical: "center" } };
            const rightAlignment = { alignment: { horizontal: "right", vertical: "center" } };

            for (const estateName in groupedByEstate) {
                let inspectionsForEstate = groupedByEstate[estateName];

                // Urutkan/Kelompokkan berdasarkan nama karyawan dalam estate tersebut
                inspectionsForEstate.sort((a, b) => {
                    if (a.name < b.name) return -1;
                    if (a.name > b.name) return 1;

                    if (a.nip < b.nip) return -1;
                    if (a.nip > b.nip) return 1;

                    if (a.block < b.block) return -1;
                    if (a.block > b.block) return 1;

                    if (a.inspection_date && b.inspection_date) {
                        return new Date(a.inspection_date).getTime() - new Date(b.inspection_date).getTime();
                    }

                    if (a.job_level < b.job_level) return 1;
                    if (a.job_level > b.job_level) return -1;

                    const pa = a.penalty_code ?? "";
                    const pb = b.penalty_code ?? "";

                    if (!pa && pb) return 1;
                    if (pa && !pb) return -1;
                    if (pa < pb) return 1;
                    if (pa > pb) return -1;

                    return 0;
                });

                let prevNip: string | null = null;
                let prevBlock: string | null = null;

                const sheetData = inspectionsForEstate.map(item => {
                    const isNewNip = item.nip !== prevNip;

                    const displayName = isNewNip ? item.name : "";
                    const displayBlock = (isNewNip || item.block !== prevBlock) ? item.block : "";

                    prevNip = item.nip;
                    prevBlock = item.block;

                    // Ganti nilai kosong dengan string kosong agar border tetap muncul
                    const safeValue = (v: any) => (v === null || v === undefined || v === "") ? "" : v;

                    return {
                        'NAMA KARYAWAN': safeValue(displayName),
                        'NIP': safeValue(item.nip),
                        'JABATAN': safeValue(item.job_title),
                        'DIVISI': safeValue(item.division),
                        'BLOK': safeValue(item.block),
                        'TANGGAL INSPEKSI': item.inspection_date
                            ? format(new Date(`${item.inspection_date}T00:00:00`), 'dd/MM/yyyy', { locale: id })
                            : '',
                        'NAMA SUPERVISOR': safeValue(item.spv_name),
                        'LEVEL SUPERVISOR': safeValue(item.job_level),
                        'JABATAN SUPERVISOR': safeValue(item.job_title_supervisor),
                        'KODE PENALTY': safeValue(item.penalty_code),
                        'DESKRIPSI PENALTY': safeValue(item.penalty_descr),
                        'QTY': item.qty ?? "",
                        'NO TPH': safeValue(item.no_tph),
                        'NO BARIS': safeValue(item.no_baris),
                        'NO POKOK': safeValue(item.no_pokok),
                        'DESKRIPSI TEMUAN': safeValue(item.description),
                    };
                });

                const ws = XLSX.utils.json_to_sheet([], { header: columnOrder });
                XLSX.utils.sheet_add_json(ws, sheetData, { skipHeader: true, origin: 'A2' });

                // Styling Header
                for (let C = 0; C < columnOrder.length; ++C) {
                    const cellAddress = XLSX.utils.encode_cell({ r: 0, c: C });
                    if (!ws[cellAddress]) ws[cellAddress] = { v: columnOrder[C] };
                    else ws[cellAddress].v = columnOrder[C];
                    ws[cellAddress].s = headerStyle;
                }

                // Styling Data Rows
                sheetData.forEach((_, rowIndex) => {
                    columnOrder.forEach((colName, colIndex) => {
                        const cellAddress = XLSX.utils.encode_cell({ r: rowIndex + 1, c: colIndex });
                        if (ws[cellAddress]) {
                            ws[cellAddress].s = { ...cellBorderStyle };

                            // Specific alignments
                            if (['NAMA KARYAWAN', 'NIP', 'JABATAN', 'DIVISI', 'BLOCK', 'NAMA SUPERVISOR', 'LEVEL SUPERVISOR', 'JABATAN SUPERVISOR', 'KODE PELANGGARAN', 'DESKRIPSI PELANGGARAN', 'DESKRIPSI TEMUAN'].includes(colName)) {
                                ws[cellAddress].s = { ...ws[cellAddress].s, ...leftAlignment };
                            } else if (['TANGGAL INSPEKSI', 'NO TPH', 'NO BARIS', 'NO POKOK'].includes(colName)) {
                                ws[cellAddress].s = { ...ws[cellAddress].s, ...centerAlignment };
                            } else if (colName === 'QTY') {
                                ws[cellAddress].s = { ...ws[cellAddress].s, ...rightAlignment };
                                if (typeof ws[cellAddress].v === 'number') {
                                    ws[cellAddress].t = 'n';
                                    ws[cellAddress].z = '#,##0';
                                }
                            }

                            // Format tanggal
                            if (colName === 'TANGGAL INSPEKSI' && ws[cellAddress].v !== '-') {
                                ws[cellAddress].z = 'dd/mm/yyyy';
                            }
                        }
                    });
                });

                // Column Widths
                ws['!cols'] = [
                    { wch: 25 }, // NAMA KARYAWAN
                    { wch: 15 }, // NIP
                    { wch: 20 }, // JABATAN
                    { wch: 20 }, // DIVISI
                    { wch: 12 }, // BLOCK
                    { wch: 15 }, // TANGGAL INSPEKSI
                    { wch: 25 }, // NAMA SUPERVISOR
                    { wch: 15 }, // LEVEL SUPERVISOR
                    { wch: 20 }, // JABATAN SUPERVISOR
                    { wch: 18 }, // KODE PELANGGARAN
                    { wch: 84 }, // DESKRIPSI PELANGGARAN
                    { wch: 8 },  // QTY
                    { wch: 10 }, // NO TPH
                    { wch: 20 }, // NO BARIS
                    { wch: 20 }, // NO POKOK
                    { wch: 215 }, // DESKRIPSI TEMUAN
                ];
                ws['!rows'] = [{ hpt: 25 }];

                // Sanitasi nama sheet
                const safeSheetName = estateName.replace(/[\\/*?[\]:]/g, "").substring(0, 30);
                XLSX.utils.book_append_sheet(wb, ws, safeSheetName);
            }

            const selectedMonthLabel = months.find(m => m.value === selectedMonth)?.label || selectedMonth;
            XLSX.writeFile(wb, `Laporan Supervisi ${selectedMonthLabel}.xlsx`);
            trackDownload('Laporan Supervisi', { month: selectedMonthLabel });
            toast({
                title: 'Success',
                description: 'Laporan supervisi berhasil diekspor',
                status: 'success',
                duration: 3000,
                isClosable: true,
            });
        } catch (error) {
            let description = 'Gagal mengekspor laporan supervisi';

            if (isAxiosError(error) && error.response) {
                description = `Gagal mengekspor laporan: ${error.response.data?.message || error.message}`;
            } else if (error instanceof Error) {
                description = `Gagal mengekspor laporan: ${error.message}`;
            }

            toast({
                title: 'Error',
                description,
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
            throw error;
        }
    }
};

export default SupervisiPinaltiAncakPanen;
